<template>
  <div class="contact">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">お問い合わせ</h1>
      </div>
    </div>
    
    <section class="section">
      <div class="container">
        <div class="contact-container">
          <div class="contact-info">
            <h2 class="contact-subtitle">教室について</h2>
            <div v-if="isLoadingAccess" class="loading-state">情報を読み込み中...</div>
            <div v-else-if="accessError" class="error-message">{{ accessError }}</div>
            <div v-else>
              <div class="info-item">
                <div class="info-icon">📍</div>
                <div class="info-text">
                  <h3>住所</h3>
                  <p class="preserve-whitespace">{{ accessInfo.address || '中崎町ホール\n〒530-0015\n大阪府大阪市北区中崎西1丁目6-8' }}</p>
                </div>
              </div>
              <div class="info-item" v-if="accessInfo.phone_number">
                <div class="info-icon">📞</div>
                <div class="info-text">
                  <h3>電話</h3>
                  <p>{{ accessInfo.phone_number }}</p>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">📧</div>
                <div class="info-text">
                  <h3>メール</h3>
                  <p>ateloetti@gmail.com</p>
                </div>
              </div>
              <div class="info-item">
                <div class="info-icon">🕒</div>
                <div class="info-text">
                  <h3>営業時間</h3>
                  <p>月曜日 - 金曜日: 10:00 - 19:00</p>
                  <p>土曜日: 10:00 - 17:00</p>
                  <p>日曜日・祝日: 休み</p>
                </div>
              </div>
              <div class="map">
                <h3>アクセス</h3>
                <p class="preserve-whitespace">{{ accessInfo.transport_info || '中崎町駅から徒歩2分' }}</p>
                <div v-if="accessInfo.google_maps_embed_url" class="map-container">
                  <iframe
                    :src="accessInfo.google_maps_embed_url"
                    width="100%"
                    height="250"
                    style="border:0; border-radius: 8px;"
                    allowfullscreen=""
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <div v-else class="map-placeholder">
                  <div class="placeholder-text">地図情報が設定されていません</div>
                </div>
              </div>
            </div>
          </div>
          <div class="contact-form">
            <h2 class="contact-subtitle">お問い合わせフォーム</h2>
            <div v-if="formSubmitted" class="success-message">
              お問い合わせありがとうございます。近日中にご連絡いたします。
            </div>
            <div v-else>
              <div v-if="formError" class="error-message">
                {{ formError }}
              </div>
              <div v-if="user" class="logged-in-notice">
                <p>{{ user.name || user.email }}様としてお問い合わせを送信します。</p>
              </div>
              <el-form ref="formRef" :model="form" label-position="top" :rules="rules">
                <el-form-item label="お名前" prop="name">
                  <el-input v-model="form.name" placeholder="例：山田 太郎"></el-input>
                </el-form-item>
                <el-form-item label="メールアドレス" prop="email">
                  <el-input v-model="form.email" placeholder="例：example@mail.com"></el-input>
                </el-form-item>
                <el-form-item label="電話番号" prop="phone">
                  <el-input v-model="form.phone" placeholder="例：090-1234-5678"></el-input>
                </el-form-item>
                <el-form-item label="お問い合わせ内容" prop="subject">
                  <el-select v-model="form.subject" placeholder="お問い合わせ内容を選択してください" style="width: 100%">
                    <el-option label="コースについて" value="コース"></el-option>
                    <el-option label="料金について" value="料金"></el-option>
                    <el-option label="無料体験について" value="無料体験"></el-option>
                    <el-option label="その他" value="その他"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="メッセージ" prop="message">
                  <el-input type="textarea" v-model="form.message" :rows="5" placeholder="ご質問やご要望を入力してください"></el-input>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" @click="submitForm" class="submit-btn" :loading="isSubmitting">送信する</el-button>
                </el-form-item>
              </el-form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// 导入需要的库
import { ref, reactive, onMounted } from 'vue';
import { authService } from '@/api/auth.js';
import { API_ENDPOINTS } from '@/api/config.js';

export default {
  name: 'ContactPage',
  setup() {
    // 用户状态
    const user = ref(null);
    const form = reactive({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    
    // 表单引用
    const formRef = ref(null);

    // 表单规则
    const rules = {
      name: [{ required: true, message: 'お名前を入力してください', trigger: 'blur' }],
      email: [
        { required: true, message: 'メールアドレスを入力してください', trigger: 'blur' },
        { type: 'email', message: '正しいメールアドレス形式で入力してください', trigger: 'blur' }
      ],
      subject: [{ required: true, message: 'お問い合わせ内容を選択してください', trigger: 'change' }],
      message: [{ required: true, message: 'メッセージを入力してください', trigger: 'blur' }]
    };

    // 表单状态
    const isSubmitting = ref(false);
    const formSubmitted = ref(false);
    const formError = ref('');

    // Access 信息状态
    const accessInfo = ref({});
    const isLoadingAccess = ref(true);
    const accessError = ref('');

    // 获取用户IP地址
    const userIp = ref('');

    // 检查用户登录状态
    const checkUserStatus = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          user.value = currentUser;
          // 预填充用户信息
          form.name = currentUser.name || '';
          form.email = currentUser.email || '';
        }
      } catch (error) {
        // 用户未登录，继续作为访客
        console.log('User not logged in');
      }
    };

    // 获取Access信息
    const fetchAccessInfo = async () => {
      isLoadingAccess.value = true;
      accessError.value = '';
      try {
        const response = await fetch(API_ENDPOINTS.SETTINGS.ACCESS);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        accessInfo.value = data;
      } catch (err) {
        console.error("アクセス情報の取得エラー:", err);
        accessError.value = "情報の読み込み中にエラーが発生しました。";
      } finally {
        isLoadingAccess.value = false;
      }
    };

    // 获取用户IP
    const fetchUserIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        userIp.value = data.ip;
      } catch (error) {
        console.error('IP取得エラー:', error);
        userIp.value = '取得できません';
      }
    };

    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return;
      
      isSubmitting.value = true;
      formError.value = '';
      
      try {
        // 表单验证
        await formRef.value.validate();
        
        // 构建消息数据
        const messageData = {
          name: form.name,
          email: form.email,
          phone: form.phone || '',
          subject: form.subject,
          message: form.message
        };
        
        // 发送到后端API
        const response = await fetch(API_ENDPOINTS.MESSAGES.CREATE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            })
          },
          body: JSON.stringify(messageData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send message');
        }
        
        // 重置表单
        form.name = user.value ? user.value.name || '' : '';
        form.email = user.value ? user.value.email || '' : '';
        form.phone = '';
        form.subject = '';
        form.message = '';
        
        // 显示成功消息
        formSubmitted.value = true;
      } catch (error) {
        console.error('お問い合わせ送信エラー:', error);
        formError.value = error.message || 'お問い合わせの送信に失敗しました。後でもう一度お試しください。';
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      // 检查用户登录状态
      await checkUserStatus();
      
      // 获取访问信息
      await fetchAccessInfo();
      
      // 获取用户IP
      await fetchUserIp();
    });

    return {
      user,
      form,
      formRef,
      rules,
      isSubmitting,
      formSubmitted,
      formError,
      accessInfo,
      isLoadingAccess,
      accessError,
      submitForm
    };
  }
}
</script>

<style scoped>
.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 60px 0;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
}

.contact-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  margin: 40px 0;
}

.contact-subtitle {
  font-size: 24px;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
}

.info-item {
  display: flex;
  margin-bottom: 30px;
}

.info-icon {
  font-size: 24px;
  margin-right: 15px;
  color: var(--primary-color);
}

.info-text h3 {
  font-size: 18px;
  margin-bottom: 8px;
  color: #333;
}

.info-text p {
  color: #666;
  margin-bottom: 5px;
}

.map {
  margin-top: 40px;
}

.map h3 {
  font-size: 18px;
  margin-bottom: 8px;
  color: #333;
}

.map p {
  color: #666;
  margin-bottom: 15px;
}

.map-container {
  height: 250px;
  overflow: hidden;
  border-radius: 10px;
}

.map-placeholder {
  height: 250px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;
}

.placeholder-text {
  color: #888;
  font-size: 16px;
}

.contact-form {
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.submit-btn {
  width: 100%;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.logged-in-notice {
  background-color: #e3f2fd;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  color: #0d47a1;
}

.success-message {
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 20px;
  border-radius: 6px;
  text-align: center;
  margin-bottom: 20px;
  font-weight: bold;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
}

.loading-state {
  text-align: center;
  padding: 20px;
  color: #666;
}

.preserve-whitespace {
  white-space: pre-wrap;
}

.user-field {
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  color: #606266;
  min-height: 40px;
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .contact-container {
    grid-template-columns: 1fr;
  }
  
  .contact-info {
    order: 2;
  }
  
  .contact-form {
    order: 1;
    margin-bottom: 40px;
  }
}
</style> 